/**
 * Pure route resolution for the hosted checkout SPA (bd bj9).
 *
 * The SPA has exactly three destinations:
 *   - `welcome`        — the generic marketing landing at `/`. Loads no tenant
 *                        and makes no checkout/config API call.
 *   - `return`         — `/return/<outcome>`, the post-HPP landing.
 *   - `checkout`       — an explicit tenant + strategy checkout.
 *   - `missing-target` — a checkout path that does NOT carry both a tenant and
 *                        a strategy. There is deliberately no hardcoded tenant
 *                        fallback; an under-specified checkout URL is an error
 *                        state, not a default Amentum checkout.
 *
 * Keeping this pure (no DOM, no fetch) makes the routing rules unit-testable
 * and keeps `App` free of branching tenant logic.
 */
export type CheckoutRoute =
  | { kind: 'welcome' }
  | { kind: 'return'; outcome: string }
  | { kind: 'checkout'; tenant: string; strategy: string; locale: string }
  | { kind: 'checkout-link'; token: string; locale: string }
  | { kind: 'missing-target' };

/**
 * @param path   `window.location.pathname`.
 * @param params `window.location.search` parsed into URLSearchParams.
 * @param defaultLocale Locale to use when no `locale` param is present.
 *   Callers pass a navigator-derived value at runtime; defaults to `en` so
 *   the function is deterministic in tests.
 */
export function resolveRoute(path: string, params: URLSearchParams, defaultLocale = 'en'): CheckoutRoute {
  if (path === '/' || path === '') return { kind: 'welcome' };

  if (path.startsWith('/return/')) {
    return { kind: 'return', outcome: path.slice('/return/'.length) || 'unknown' };
  }

  // Opaque shareable checkout URL `/c/<token>` — the tenant is NEVER in the
  // URL; it's resolved server-side from the link token.
  if (path.startsWith('/c/')) {
    const token = path.slice('/c/'.length);
    if (token) return { kind: 'checkout-link', token, locale: params.get('locale') ?? defaultLocale };
    return { kind: 'missing-target' };
  }

  const tenant = params.get('tenant');
  const strategy = params.get('strategy');
  if (!tenant || !strategy) return { kind: 'missing-target' };

  return {
    kind: 'checkout',
    tenant,
    strategy,
    locale: params.get('locale') ?? defaultLocale,
  };
}
